import React, { useState, useEffect, useRef } from 'react';
import { LoginPageProps } from '../../../types';
import PhoneInput from '../../../components/PhoneInput';
import MathCaptcha, { MathCaptchaRef } from '../../../components/MathCaptcha';
import TermsAndConditions from '../../../components/TermsAndConditions';

type MobileLoginNewProps = LoginPageProps;

const MobileLoginNew: React.FC<MobileLoginNewProps> = ({
  formData,
  isLoading,
  error,
  onInputChange,
  onSMSLogin,
  onMTSCLogin,
  onFNSLogin,
  onToggleTerms,
  onToggleOtherMethods,
  onVerifySMSCode,
  onResetCaptcha,
}) => {
  const [showSmsCodePanel, setShowSmsCodePanel] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [currentPhone, setCurrentPhone] = useState('');
  const smsCodeInputRef = useRef<HTMLInputElement>(null);
  const mathCaptchaRef = useRef<MathCaptchaRef>(null);

  // Автофокус на поле ввода SMS кода при открытии панели
  useEffect(() => {
    if (showSmsCodePanel && smsCodeInputRef.current) {
      smsCodeInputRef.current.focus();
    }
  }, [showSmsCodePanel]);

  const handleSMSLoginClick = async () => {
    if (!formData.phoneNumber || !formData.captchaAnswer || 
        !formData.acceptTerms || !formData.acceptPrivacy || 
        !formData.acceptPersonalData || !formData.acceptPlatformTerms) {
      return; // Валидация уже есть в onSMSLogin
    }
    
    // Вызываем основную логику SMS логина из LoginPage
    await onSMSLogin();
    
    // Если onSMSLogin успешен, показываем панель ввода кода
    setCurrentPhone(formData.phoneNumber);
    setShowSmsCodePanel(true);
  };

  const handleVerifySmsCode = async () => {
    if (!smsCode.trim()) return;
    
    try {
      // Вызываем верификацию SMS кода через LoginPage
      // Передаем только код, номер телефона уже известен в LoginPage
      await onVerifySMSCode(smsCode);
      
      // Если верификация успешна, панель закроется автоматически
      // и произойдет редирект на dashboard
      setShowSmsCodePanel(false);
      setSmsCode('');
    } catch (error) {
      console.error('Error verifying SMS code:', error);
    }
  };










  return (
    <div className="min-h-screen bg-[#141826] flex flex-col relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute w-full h-full left-0 top-0 pointer-events-none">
        {/* Ellipse 1 - Top Left */}
        <div 
          className="absolute w-[180px] h-[100px] left-[-80px] top-[80px] bg-[rgba(118,97,215,0.3)] opacity-80 blur-[35px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(118,97,215,0.4) 0%, rgba(118,97,215,0.1) 70%, transparent 100%)',
            filter: 'blur(35px)'
          }}
        />
        
        {/* Ellipse 2 - Top Right */}
        <div 
          className="absolute w-[160px] h-[120px] right-[-60px] top-[120px] bg-[rgba(23,12,90,0.4)] opacity-70 blur-[30px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(23,12,90,0.5) 0%, rgba(23,12,90,0.2) 60%, transparent 100%)',
            filter: 'blur(30px)'
          }}
        />
        
        {/* Ellipse 3 - Bottom Left */}
        <div 
          className="absolute w-[140px] h-[180px] left-[-50px] bottom-[120px] bg-[rgba(118,97,215,0.25)] opacity-60 blur-[25px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(118,97,215,0.3) 0%, rgba(118,97,215,0.1) 50%, transparent 100%)',
            filter: 'blur(25px)'
          }}
        />
        
        {/* Ellipse 4 - Bottom Right */}
        <div 
          className="absolute w-[120px] h-[100px] right-[-30px] bottom-[150px] bg-[rgba(23,12,90,0.3)] opacity-50 blur-[20px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(23,12,90,0.4) 0%, rgba(23,12,90,0.15) 65%, transparent 100%)',
            filter: 'blur(20px)'
          }}
        />
      </div>

      {/* Header */}
      <div className="px-4 py-6 flex items-center justify-between relative z-10">
        <div className="w-12 h-12 bg-[#7661d7] rounded-full flex items-center justify-center">
          <span className="text-white text-xl">A</span>
        </div>
                 <div className="text-white text-2xl">Сделка и Точка</div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 pb-6 relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-white text-3xl mb-2">Добро пожаловать!</h2>
          <p className="text-slate-400 text-sm mb-3">
            Сервис для безопасных расчётов: создайте сделку, отправьте ссылку покупателю. После оплаты обе стороны подтверждают сделку — деньги поступают на вашу карту.
          </p>
          <p className="text-slate-500 text-xs">
            Войдите, чтобы создавать сделки и управлять выплатами.
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 mb-6">
          {/* Phone Number Input */}
          <div className="mb-6">
            <label htmlFor="phone" className="block text-white text-sm mb-2">
              Номер телефона
            </label>
            <PhoneInput
              value={formData.phoneNumber}
              onChange={(value) => onInputChange('phoneNumber', value)}
              label=""
              required
              error={!!error}
              disabled={isLoading}
            />
          </div>

                     {/* Captcha Input */}
                       <div className="mb-6">
              <MathCaptcha
                ref={mathCaptchaRef}
                value={formData.captchaAnswer}
                onChange={(value) => onInputChange('captchaAnswer', value)}
                label="Решите пример"
                required
                error={!!error}
                disabled={isLoading}
              />
            </div>

          {/* Terms and Conditions */}
          <div className="mb-6">
            <TermsAndConditions
              terms={{
                acceptTerms: formData.acceptTerms,
                acceptPrivacy: formData.acceptPrivacy,
                acceptPersonalData: formData.acceptPersonalData,
                acceptPlatformTerms: formData.acceptPlatformTerms
              }}
              onTermsChange={onInputChange}
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 mb-4">
              <div className="text-red-400 text-sm">{error}</div>
            </div>
          )}

          {/* Login Methods */}
          <div className="space-y-3">
            {/* SMS Login Button */}
                         <button
               onClick={handleSMSLoginClick}
               disabled={isLoading}
               className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isLoading ? (
                 <span>Вход...</span>
               ) : (
                 <span>Войти по SMS</span>
               )}
             </button>

                         {/* MTS ID Button */}
             {/* <button
               onClick={onMTSCLogin}
               disabled={isLoading}
               className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-slate-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isLoading ? (
                 <span>Вход...</span>
               ) : (
                 <span>Войти через MTS ID</span>
               )}
             </button> */}

             {/* FNS Button */}
             {/* <button
               onClick={onFNSLogin}
               disabled={isLoading}
               className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-slate-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isLoading ? (
                 <span>Войти через ФНС</span>
               ) : (
                 <span>Войти через ФНС</span>
               )}
             </button> */}
          </div>
        </div>

        {/* Info Panel */}
        <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <span className="text-gray-800 text-sm">i</span>
              </div>
            </div>
            <div className="flex-1 text-white text-sm leading-tight">
              Войдите по SMS — затем вы сможете создавать сделки, отправлять ссылки на оплату и получать деньги на карту после подтверждения.
            </div>
          </div>
                 </div>
       </div>

       {/* Панель для ввода SMS кода */}
       {showSmsCodePanel && (
         <div className="fixed inset-0 z-50 flex items-end justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-black/50 backdrop-blur-sm"
             onClick={() => setShowSmsCodePanel(false)}
           />
           
           {/* Panel */}
           <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
             {/* Header */}
             <div className="text-white text-left p-4 relative border-b border-white/10">
               <button
                 onClick={() => setShowSmsCodePanel(false)}
                 className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
               >
                 ✕
               </button>
               <h3 className="text-2xl">Введите код из SMS</h3>
               <p className="text-slate-400 text-sm mt-2">
                 Код отправлен на номер {currentPhone}
               </p>
             </div>
             
             {/* Body */}
             <div className="text-white p-4">
               <div className="mb-6">
                 <label htmlFor="sms-code" className="block text-white text-sm mb-2">
                   Код из SMS
                 </label>
                 <input
                   ref={smsCodeInputRef}
                   type="text"
                   id="sms-code"
                   value={smsCode}
                   onChange={(e) => setSmsCode(e.target.value)}
                   placeholder="Введите код"
                   className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-white/20"
                   maxLength={6}
                 />
               </div>
               
               <button
                 onClick={handleVerifySmsCode}
                 disabled={!smsCode.trim()}
                 className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 <span>Подтвердить</span>
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default MobileLoginNew;
